import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaTimes, FaShareAlt, FaClipboardCheck, FaStop, FaPlay } from 'react-icons/fa';
import { stopLivestream, getLivestreamById, joinLivestream, getLivestreamByIdPublic, startLivestream, isJoinResponseGranted, isJoinResponsePaymentRequired, getJoinResponseLivestream, getJoinResponseTag, getJoinResponseSessionUrl, parseJoinLivestreamResponse } from '../../utils/livestreamApi';
import { handleLivestreamError, LIVESTREAM_ERRORS } from '../../utils/livestreamErrorHandler';
import { fetchUserQuota } from '../../store/subscriptionSlice';
import aktureWatermark from '../../assets/images/aktureWatermark.png';
import { useParams } from 'react-router-dom';
import useScoreboard from '../../hooks/useScoreboard';
import useLivestreamPermissions from '../../hooks/useLivestreamPermissions';
import { useLivestreamStatusUpdates } from '../../hooks/useLivestreamStatusUpdates';
import Scoreboard from '../Scoreboard/Scoreboard';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingSpinner from '../common/LoadingSpinner';
import HlsPlayer from './HlsPlayer';
import StreamActionButton from './StreamActionButton';
import {
  Card,
  TitleRow,
  Title,
  IconButton,
  VideoWrap,
  ControlsPanel,
  Watermark,
  WatermarkImg,
  SponsorsRow,
  SponsorsTitle,
  SponsorsIcons,
  SponsorItem,
  SponsorLogo,
  SponsorName,
  InfoText,
  Footer,
  LoadingContainer,
  LoadingText,
  ErrorContainer,
  ErrorMessage,
  RetryButton,
  ScoreOverlay,
  ShareButton,
  MobileVideoContainer
} from './LivestreamWatchPage.styles';
import ScoreControls from '../Scoreboard/ScoreControls';
import FullscreenOverlay from '../common/FullscreenOverlay';

// Add error mapping to match backend error types
const ERROR_MESSAGES = {
  LIVESTREAM_NOT_FOUND: 'Livestream not found',
  LIVESTREAM_UNAUTHORIZED_ACCESS: 'You are not authorized to access this livestream',
  LIVESTREAM_FORBIDDEN: 'You do not have permission to access this livestream',
  LIVESTREAM_PAYMENT_REQUIRED: 'Payment is required to watch this livestream',
  LIVESTREAM_NOT_IN_PROGRESS: 'This livestream is not in progress yet',
  LIVESTREAM_ALREADY_COMPLETED: 'This livestream has already ended',
  LIVESTREAM_INVALID_STATUS: 'Invalid livestream status',
  UNAUTHORIZED: 'Please log in to access this livestream',
  USER_NOT_FOUND: 'User not found',
  COURT_NOT_FOUND: 'Court not found',
  CAMERA_NOT_FOUND: 'Camera not found',
  LIVESTREAM_WATCH_URL_MISSING: 'Stream is not available yet',
  LIVESTREAM_EXPIRED: 'This livestream has expired',
  LIVESTREAM_ALREADY_CANCELLED: 'This livestream has been cancelled',
  LIVESTREAM_ALREADY_IN_PROGRESS: 'This livestream is already in progress',
  VIEWER_ALREADY_JOINED: 'You have already joined this livestream',
  LIVESTREAM_VIEW_LIMIT_REACHED: 'Viewer limit reached for this livestream',
  LIVESTREAM_START_AFTER_END: 'Cannot start livestream after end time',
  LIVESTREAM_DESCRIPTION_EMPTY: 'Livestream description is required',
  LIVESTREAM_LOGO_EMPTY: 'Livestream logo is required',
  LIVESTREAM_SPONSORS_EMPTY: 'At least one sponsor is required',
  LIVESTREAM_PRICE_NEGATIVE: 'Livestream price cannot be negative',
  LIVESTREAM_UPDATE_NULL: 'No updates provided',
  TEAM_INFO_MISSING: 'Team information is missing',
  COURT_OR_OWNER_NULL: 'Court or owner information is missing'
};

function LivestreamWatchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { livestreamId } = useParams();
  const navLivestream = location.state?.livestream;
  const isMounted = useRef(true); // Reference to the mounted state e.g. if the component is mounted or not
  const abortController = useRef(null); // Reference to the abort controller e.g. if the request is aborted or not
  const shareLink = useRef(null); // Reference to the share link input
  const [isCopied, setIsCopied] = useState(false); // State for copied status e.g. if the link is copied or not
  // Get auth state from Redux only
  const { user, isLoading: isAuthLoading } = useSelector((state) => state.auth);
  const isAuthenticated = !!user;
  
  // State for livestream management
  const [livestream, setLivestream] = useState(navLivestream || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [hasAttemptedJoin, setHasAttemptedJoin] = useState(false);

  // Define isStreamReady before using it in scoreboardProps
  const isStreamReady = useMemo(() =>
    livestream?.status === 'IN_PROGRESS' && livestream?.watchUrl
  , [livestream?.status, livestream?.watchUrl]);

  // Show loading state only if we're loading AND don't have a livestream
  // OR if we have a livestream but it's not ready yet (and user has access)
  const shouldShowLoading = (isLoading && !livestream) || 
    (!isStreamReady && livestream?.status === 'IN_PROGRESS' && 
     (livestream?.requiresPayment ? 
       (livestream?.liveStreamPurchaseIds ?? []).includes(user?.id) || 
       livestream?.court?.owner?.id === user?.id || 
       user?.roles?.includes('ADMIN') : true));

  // Log livestream object for debugging
  useEffect(() => {
    if (livestream) {
      // Livestream logging removed for production
    }
  }, [livestream]);

  // Debug logging for loading state
  useEffect(() => {
    if (livestream) {
      // Loading state logging removed for production
    }
  }, [livestream, isLoading, isStreamReady]);

  // Memoize the livestreamId for scoreboard to prevent unnecessary re-renders
  const scoreboardLivestreamId = useMemo(() => {
    if (livestream?.status === 'IN_PROGRESS' && 
        livestream?.gameType && 
        ['PICKLEBALL', 'SOCCER'].includes(livestream.gameType)) {
      return livestream.id;
    }
    return null;
  }, [livestream?.id, livestream?.status, livestream?.gameType]);

  // Get permissions and scoreboard
  const {
    canManageLivestream,
    canStopLivestream,
    canUpdateScore,
    canJoinLivestream,
    canStartLivestream
  } = useLivestreamPermissions(livestream);

  const { 
    score, 
    updateScore: handleScoreUpdate, 
    resetScore: handleResetScore, 
    isInitialized: isScoreboardInitialized 
  } = useScoreboard(scoreboardLivestreamId);

  // Real-time status updates
  const handleStatusUpdate = useCallback((statusUpdate) => {
    
    if (statusUpdate.type === 'viewer_update') {
      // Update viewer count
      setLivestream(prev => ({
        ...prev,
        viewerCount: statusUpdate.viewerCount,
        viewerIds: statusUpdate.viewerIds
      }));
    } else {
      // Update livestream status
      setLivestream(prev => ({
        ...prev,
        status: statusUpdate.status,
        watchUrl: statusUpdate.watchUrl || prev.watchUrl,
        endTime: statusUpdate.endTime || prev.endTime
      }));
    }
  }, []);

  const { isConnected: isStatusConnected } = useLivestreamStatusUpdates(
    livestreamId, 
    handleStatusUpdate, 
    !!livestreamId && !!user
  );

  // Memoize the scoreboard props to prevent unnecessary re-renders
  const scoreboardProps = useMemo(() => ({
    score,
    onUpdateScore: handleScoreUpdate,
    onResetScore: handleResetScore,
    canUpdate: canUpdateScore,
    isLoading: !isScoreboardInitialized && isStreamReady
  }), [
    score,
    handleScoreUpdate,
    handleResetScore,
    canUpdateScore,
    isScoreboardInitialized,
    isStreamReady
  ]);

  // Add a check for game type
  const shouldShowScoreboard = useMemo(() => 
    livestream?.status === 'IN_PROGRESS' && 
    livestream?.gameType && 
    ['PICKLEBALL', 'SOCCER'].includes(livestream.gameType)
  , [livestream?.status, livestream?.gameType]);

  // Memoize the share URL
  const shareUrl = useMemo(() => {
    if (!livestream) return '';
    return livestream.requiresPayment
      ? `${window.location.origin}/livestream/watch/${livestreamId}`     // paid -> private page
      : `${window.location.origin}/public/livestream/watch/${livestreamId}`; // free/public -> public page
  }, [livestream, livestreamId]);

  // Effect for auth check and redirect
  useEffect(() => {
    // Only redirect to login if the livestream requires payment and user is not authenticated
    if (!isAuthLoading && !isAuthenticated && livestream?.requiresPayment) {
      navigate('/login', { state:{ from: location.pathname }});
    }
  }, [isAuthLoading, isAuthenticated, livestream?.requiresPayment, navigate, location.pathname]);

  // Define fetchLivestream function
  const fetchLivestream = useCallback(async () => {
    if (!livestreamId) {
  
      return;
    }
    
    try {
      if (!livestream) {
        setIsLoading(true);
      }
      
      abortController.current = new AbortController(); 
      
      // Use the appropriate API call based on auth status
      const response = isAuthenticated 
        ? await getLivestreamById(livestreamId, abortController.current.signal) 
        : await getLivestreamByIdPublic(livestreamId, abortController.current.signal);

      if (!response) {
        throw new Error('No response received from server');
      }

      if (isMounted.current) {
        console.log('[LivestreamWatchPage] fetchLivestream - Received livestream response:', response);
        console.log('[LivestreamWatchPage] Livestream object details:', {
          id: response.id,
          description: response.description,
          status: response.status,
          startTime: response.startTime,
          endTime: response.endTime,
          price: response.price,
          requiresPayment: response.requiresPayment,
          courtId: response.courtId,
          sponsors: response.sponsors,
          team: response.teamAName && response.teamBName ? {
            teamA: response.teamAName,
            teamB: response.teamBName,
            teamAScore: response.teamAScore,
            teamBScore: response.teamBScore
          } : null,
          watchUrl: response.watchUrl,
          playbackUrl: response.playbackUrl
        });
        
        setLivestream(response);
        setError(null);
        setIsLoading(false);
      }
    } catch (err) {
      if (isMounted.current) {
        if (err.name === 'AbortError') {
          return;
        }
        setError(handleLivestreamError(err));
        setIsLoading(false);
        if (err.message === LIVESTREAM_ERRORS.PERMISSION_DENIED) {
          navigate('/explore');
        }
      }
    }
  }, [livestreamId, isAuthenticated, livestream, navigate]);

  // Effect for initial fetch and cleanup
  useEffect(() => {
       // Only call when we mount OR the id changes
      if (!livestream || String(livestream.id) !== String(livestreamId)) {
        fetchLivestream();
      } else {
        setIsLoading(false);
      }
    }, [livestreamId, fetchLivestream, livestream]);


  // Reset payment confirmation state when livestream changes
  useEffect(() => {
    setPaymentConfirmed(false);
    setHasAttemptedJoin(false);
  }, [livestreamId]);

  // Effect for Stripe session confirmation (for viewers after payment)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    const handleStripeRedirect = async () => {
      if (sessionId && !paymentConfirmed) {
        // Payment confirmation is handled by webhook, just show success and refresh
        setPaymentConfirmed(true); // Mark as confirmed to prevent repeated processing
        toast.success('Payment confirmed! You can now watch the livestream.');
        
        // Wait a moment for webhook to process the payment
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Refresh livestream data to reflect the user's new purchase status
        await fetchLivestream(true); // Force refresh
        
        // Refresh user quota after successful payment
        if (user?.id) {
          dispatch(fetchUserQuota(user.id));
        }
        
        // Remove session_id from URL to prevent re-processing
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('session_id');
        window.history.replaceState({}, '', newUrl);
      }
    };

    handleStripeRedirect();
  }, [livestreamId, location.search, fetchLivestream, paymentConfirmed]);

  // Update error handling to use backend error types
  const handleStreamError = useCallback((error) => {
    
    // Check if error is from backend API
    if (error.response?.data?.errorType) {
      const errorType = error.response.data.errorType;
      const message = ERROR_MESSAGES[errorType] || error.response.data.message || 'An error occurred';
      
      // Handle specific error types
      switch (errorType) {
        case 'LIVESTREAM_PAYMENT_REQUIRED':
        case 'UNAUTHORIZED':
          navigate('/login', { state: { from: location.pathname } });
          break;
        case 'LIVESTREAM_NOT_FOUND':
        case 'LIVESTREAM_EXPIRED':
        case 'LIVESTREAM_ALREADY_CANCELLED':
        case 'LIVESTREAM_ALREADY_COMPLETED':
          navigate('/explore');
          break;
        default:
          toast.error(message);
          if (errorType.includes('UNAUTHORIZED') || errorType.includes('FORBIDDEN')) {
            navigate('/explore');
          }
      }
      return;
    }

    // Handle client-side errors
    const errorMessage = typeof error === 'string' ? error : error?.message || 'An error occurred';
    if (errorMessage.includes('not live') || errorMessage.includes('not available')) {
      toast.warning('Stream is not available yet. Please try again in a few moments.');
      return;
    }
    toast.error(errorMessage);
    navigate('/explore');
  }, [navigate, location.pathname]);

  // Update join stream handler to handle paid livestreams properly
  const handleJoinStream = useCallback(async () => {
    if (!livestream || !user || isJoining) return;

    // Check if this is a paid livestream that requires upfront payment
    // A livestream is paid if it requires payment AND has a viewersPrice > 0
    // OR if it requires payment but viewersPrice is null (should be treated as paid)
    const isPaidLivestream = livestream.requiresPayment && 
      (livestream.viewersPrice !== null ? livestream.viewersPrice > 0 : true);
    
    // Check if user has purchased access (handle both string and number IDs)
    const purchaseIds = livestream.liveStreamPurchaseIds ?? [];
    const userId = user.id;
    const hasPurchasedAccess = purchaseIds.some(purchaseId => 
      String(purchaseId) === String(userId) || Number(purchaseId) === Number(userId)
    );
    
    console.log('🔍 WatchPage Payment Check:', {
      livestreamId: livestream.id,
      userId: userId,
      isPaidLivestream,
      requiresPayment: livestream.requiresPayment,
      price: livestream.price,
      viewersPrice: livestream.viewersPrice,
      priceType: typeof livestream.price,
      viewersPriceType: typeof livestream.viewersPrice,
      priceIsNull: livestream.price === null,
      viewersPriceIsNull: livestream.viewersPrice === null,
      priceIsZero: livestream.price === 0,
      viewersPriceIsZero: livestream.viewersPrice === 0,
      purchaseIds: purchaseIds,
      hasPurchasedAccess,
      userRoles: user?.roles,
      isAdmin: user?.roles?.includes('ADMIN'),
      isSiteOwner: user?.roles?.includes('SITE_OWNER'),
      courtOwnerId: livestream.court?.owner?.id,
      isCourtOwner: livestream.court?.owner?.id === userId,
      livestreamStatus: livestream.status,
      canManageLivestream: user?.roles?.includes('ADMIN') || (user?.roles?.includes('SITE_OWNER') && livestream.court?.owner?.id === userId)
    });
    
    if (isPaidLivestream && !hasPurchasedAccess) {
      console.log('💰 WatchPage Payment Required - Attempting to join to get payment response');
      // For paid livestreams, try to join first to get proper payment response from backend
      try {
        setIsJoining(true);
        const result = await joinLivestream(livestream.id);
        const parsedResult = parseJoinLivestreamResponse(result);
        
        console.log('🔍 WatchPage Parsed Join Result:', parsedResult);
        
        if (parsedResult.isPaymentRequired && parsedResult.sessionUrl) {
          console.log('🔗 WatchPage Redirecting to payment session URL:', parsedResult.sessionUrl);
          window.location.href = parsedResult.sessionUrl;
        } else if (parsedResult.isGranted) {
          console.log('✅ WatchPage Successfully joined stream');
          // Don't show toast for auto-join, only for manual joins
          await fetchLivestream();
        } else {
          console.error('❌ WatchPage Unexpected join result:', parsedResult);
          handleStreamError(new Error('Unexpected response from server'));
        }
      } catch (error) {
        console.error('❌ WatchPage Error during join:', error);
        handleStreamError(error);
      } finally {
        setIsJoining(false);
      }
      return;
    }

    // For free livestreams or already purchased, proceed with join
    console.log('🆓 WatchPage Free/Already Purchased - Attempting to join livestream');
    try {
      setIsJoining(true);
      const result = await joinLivestream(livestream.id);
      const parsedResult = parseJoinLivestreamResponse(result);
      
      console.log('🔍 WatchPage Parsed Join Result:', parsedResult);
      
      if (parsedResult.isPaymentRequired && parsedResult.sessionUrl) {
        console.log('🔗 WatchPage Redirecting to payment session URL:', parsedResult.sessionUrl);
        window.location.href = parsedResult.sessionUrl;
      } else if (parsedResult.isGranted) {
        console.log('✅ WatchPage Successfully joined stream');
        // Don't show toast for auto-join, only for manual joins
        await fetchLivestream();
      } else {
        console.error('❌ WatchPage Unexpected join result:', parsedResult);
        handleStreamError(new Error('Unexpected response from server'));
      }
    } catch (error) {
      console.error('❌ WatchPage Error during join:', error);
      handleStreamError(error);
    } finally {
      setIsJoining(false);
    }
  }, [livestream, user, isJoining, fetchLivestream, handleStreamError]);

  // Auto-join effect: automatically attempt to join when livestream is loaded and user is authenticated
  useEffect(() => {
    if (livestream && user && isAuthenticated && !isJoining && !paymentConfirmed && !hasAttemptedJoin) {
      console.log('🔄 Auto-join: Livestream loaded, attempting to join automatically');
      
      // Only auto-join if the stream is in progress or scheduled
      if (livestream.status === 'IN_PROGRESS' || livestream.status === 'SCHEDULED') {
        setHasAttemptedJoin(true);
        handleJoinStream();
      }
    }
  }, [livestream, user, isAuthenticated, isJoining, paymentConfirmed, hasAttemptedJoin, handleJoinStream]);

  // Add stop stream handler
  const handleStopStream = async () => {
    if (!livestream || !user || isStopping || livestream.status !== 'IN_PROGRESS') return;

    try {
      setIsStopping(true);
      await stopLivestream(livestream.id);
      toast.success('Stream stopped successfully');
      await fetchLivestream();
    } catch (error) {
      handleStreamError(error);
    } finally {
      setIsStopping(false);
    }
  };

  // Add start stream handler for owners
  const handleStartStream = async () => {
    if (!livestream || !user || livestream.status !== 'SCHEDULED') return;

    try {
      const response = await startLivestream(livestream.id);
      
      if (isJoinResponseGranted(response)) {
        const livestreamData = getJoinResponseLivestream(response);
        const tag = getJoinResponseTag(response);
        
        if (livestreamData?.watchUrl) {
          // Update local state with the new livestream data
          setLivestream(livestreamData);
          toast.success(`Livestream started successfully! (${tag})`);
        } else {
          // Refresh to get updated data
          await fetchLivestream();
          toast.success(`Livestream started successfully! (${tag})`);
        }
      } else if (isJoinResponsePaymentRequired(response)) {
        const sessionUrl = getJoinResponseSessionUrl(response);
        if (sessionUrl) {
          window.open(sessionUrl, '_blank', 'noopener');
          toast.info('Payment required. Please complete payment to start the stream.');
        } else {
          toast.error('Payment required but no payment URL provided.');
        }
      } else {
        toast.error('Could not start stream. Please try again.');
      }
    } catch (error) {
      handleStreamError(error);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Show loading state while auth is initializing
  if (isAuthLoading) {

    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading authentication...</LoadingText>
      </LoadingContainer>
    );
  }

  if (shouldShowLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>
          {!livestream ? 'Loading livestream...' : 'Preparing stream...'}
        </LoadingText>
      </LoadingContainer>
    );
  }

  // Show payment required for paid livestreams that user hasn't purchased
  const entitled = (livestream?.liveStreamPurchaseIds ?? []).includes(user?.id);
  if (livestream?.status === 'IN_PROGRESS' && 
      livestream?.requiresPayment && 
      livestream?.price > 0 &&
      !entitled &&
      livestream?.court?.owner?.id !== user?.id &&
      !user?.roles?.includes('ADMIN')) {
    // Payment required logging removed for production
    return (
      <ErrorContainer>
        <ErrorMessage>
          <h3>Payment Required</h3>
          <p>This livestream requires payment to watch.</p>
          <p>Price: ${livestream.price}</p>
          <StreamActionButton 
            livestream={livestream}
            user={user}
            onSuccess={() => window.location.reload()}
          />
        </ErrorMessage>
      </ErrorContainer>
    );
  }

  // Show error state
  if (error) {

    return (
      <ErrorContainer>
        <ErrorMessage>{typeof error === 'string' ? error : error?.message || 'An error occurred'}</ErrorMessage>
        <RetryButton onClick={() => navigate('/explore')}>
          Back to Explore
        </RetryButton>
      </ErrorContainer>
    );
  }

  // Show not found state
  if (!livestream) {

    return (
      <ErrorContainer>
        <ErrorMessage>Livestream not found</ErrorMessage>
        <RetryButton onClick={() => navigate('/explore')}>
          Back to Explore
        </RetryButton>
      </ErrorContainer>
    );
  }

  return (
    <ErrorBoundary>
      <Card>
        <TitleRow>
          <Title>{livestream.description || 'Live Stream'}</Title>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Share button in title row for better visibility */}
            <ShareButton
              onClick={copyLink}
              disabled={!livestream}
              title={shareUrl}
              data-tooltip={shareUrl}
            >
              {isCopied ? (
                <>
                  <FaClipboardCheck />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <FaShareAlt />
                  <span>Copy Link</span>
                </>
              )}
            </ShareButton>
            {/* Only show close button for court owner or admin */}
          {canManageLivestream && (
            <IconButton onClick={() => navigate('/explore')}>
              <FaTimes />
            </IconButton>
          )}
          </div>
        </TitleRow>
        <MobileVideoContainer>
        <VideoWrap>
          {livestream.status === 'IN_PROGRESS' ? (
            <HlsPlayer 
              hlsUrl={livestream.watchUrl} 
              status={livestream.status}
              autoPlay 
              onError={handleStreamError}
              scoreboard={
                shouldShowScoreboard ? (
                  score.status === 'ERROR' ? (
                    <ErrorMessage>{score.error || 'Failed to load scoreboard'}</ErrorMessage>
                  ) : (
                    <Scoreboard {...scoreboardProps} />
                  )
                ) : null
              }
            />
          ) : livestream.status === 'SCHEDULED' ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              background: '#000',
              color: '#fff',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                🎥 Livestream Scheduled
              </div>
              <div style={{ fontSize: '1rem', marginBottom: '2rem', opacity: 0.8 }}>
                {livestream.description || 'No description available'}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.6 }}>
                Start Time: {new Date(livestream.startTime).toLocaleString()}
              </div>
              {livestream.endTime && (
                <div style={{ fontSize: '0.9rem', opacity: 0.6 }}>
                  End Time: {new Date(livestream.endTime).toLocaleString()}
                </div>
              )}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              background: '#000',
              color: '#fff',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                📺 Livestream {livestream.status}
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.8 }}>
                {livestream.description || 'No description available'}
              </div>
            </div>
          )}
          <Watermark>
            <WatermarkImg src={aktureWatermark} alt="Akture Watermark" />
          </Watermark>
          </VideoWrap>
          <ControlsPanel>
            {/* Only show start button for owners when stream is scheduled */}
            {canStartLivestream && livestream.status === 'SCHEDULED' && (
              <StreamActionButton
                livestream={livestream}
                user={user}
                onClick={handleStartStream}
                buttonVariant="start"
              >
                <FaPlay />
                <span>Start Stream</span>
              </StreamActionButton>
            )}
            {/* Only show join button if user hasn't joined and has permission */}
            {canJoinLivestream && !livestream.viewers?.some(v => v.id === user?.id) && (
              <StreamActionButton
                livestream={livestream}
                user={user}
                onClick={handleJoinStream}
                disabled={isJoining}
                buttonVariant="join"
              >
                {isJoining ? 'Joining...' : 'Join Stream'}
              </StreamActionButton>
            )}
            {/* Only show stop button for the specific court owner when stream is in progress */}
            {canStopLivestream && livestream.status === 'IN_PROGRESS' && (
              <StreamActionButton
                livestream={livestream}
                user={user}
                onClick={handleStopStream}
                disabled={isStopping}
                buttonVariant="stop"
              >
                {isStopping ? (
                  'Stopping...'
                ) : (
                  <>
                    <FaStop />
                    <span>Stop Stream</span>
                  </>
                )}
              </StreamActionButton>
            )}
            {/* Only show score controls for court owner when game type is selected */}
            {shouldShowScoreboard && canUpdateScore && !score.isGameEnded && !document.fullscreenElement && (
              <ScoreControls
                onUpdateScore={handleScoreUpdate}
                disabled={!isStreamReady}
              />
            )}
            {/* Only show stream action button for court owner or admin */}
            {canManageLivestream && (
            <StreamActionButton
              livestream={livestream}
              user={user}
              onSuccess={fetchLivestream}
            />
            )}
          </ControlsPanel>
        </MobileVideoContainer>
        {livestream.sponsors?.length > 0 && (
          <SponsorsRow>
            <SponsorsTitle>Sponsored By</SponsorsTitle>
            <SponsorsIcons>
              {livestream.sponsors.map((sponsor, index) => (
                <SponsorItem key={index}>
                  <SponsorLogo
                  src={sponsor.logoUrl}
                  alt={sponsor.sponsorName}
                  title={sponsor.sponsorName}
                />
                  <SponsorName>{sponsor.sponsorName}</SponsorName>
                </SponsorItem>
              ))}
            </SponsorsIcons>
          </SponsorsRow>
        )}
        <Footer>
          <InfoText>
            {livestream.description}
          </InfoText>
        </Footer>
      </Card>
    </ErrorBoundary>
  );
}

export default React.memo(LivestreamWatchPage);

